// theme switch
(() => {
  // Cache a reference to the <html> element so we can set a global attribute on it.
  const rootElement = document.documentElement;

  // If the page does not opt into theming, do nothing.
  if (!rootElement.hasAttribute('data-scheme')) return;

  // LocalStorage key used to persist the user's explicit theme choice.
  // Note: removing this key means "follow system preference".
  const storageKey = 'theme-switch';

  // Collect all theme radio inputs (same name="theme") into a real array for easy iteration.
  const radios = Array.from(document.querySelectorAll('input[type="radio"][name="theme"]'));

  // If no theme radios exist on the page, there is nothing to initialize.
  if (!radios.length) return;

  // Media query that reflects the operating system / browser preferred color scheme.
  const mediaSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');

  // Feature flag: only enable "system" mode if there is a radio with value="system".
  const hasSystemOption = radios.some(r => r.value === 'system');

  // Determine the current system theme as a string we can apply to the DOM.
  function getSystemScheme() {
    return mediaSchemeQuery.matches ? 'dark' : 'light';
  }

  // Apply the chosen scheme by setting an attribute on <html>.
  // If the browser supports View Transitions, wrap the change to animate the transition.
  function applyScheme(scheme) {
    const apply = () => {
      rootElement.setAttribute('data-scheme', scheme);
    };

    if (typeof document.startViewTransition === 'function') document.startViewTransition(apply);
    else apply();
  }

  // Ensure exactly one radio is marked as active, both via the DOM property and the attribute.
  function checkRadio(value) {
    radios.forEach(r => {
      const isActive = r.value === value;
      r.checked = isActive;
      r.toggleAttribute('checked', isActive);
    });
  }

  // Return the currently selected radio value.
  function getCheckedValue() {
    const checked = radios.find(r => r.checked || r.hasAttribute('checked'));
    return checked ? checked.value : null;
  }

  // React to system theme changes.
  function onSystemChange(e) {
    const saved = localStorage.getItem(storageKey);

    if (saved === 'dark' || saved === 'light') return;

    if (hasSystemOption && getCheckedValue() === 'system') {
      applyScheme(e.matches ? 'dark' : 'light');
    }
  }

  // Central state setter for theme mode.
  function setMode(mode) {
    if (mode === 'dark' || mode === 'light') {
      localStorage.setItem(storageKey, mode);
      applyScheme(mode);
      checkRadio(mode);
      mediaSchemeQuery.removeEventListener('change', onSystemChange);
      return;
    }

    if (mode === 'system' && hasSystemOption) {
      localStorage.removeItem(storageKey);
      applyScheme(getSystemScheme());
      checkRadio('system');
      mediaSchemeQuery.removeEventListener('change', onSystemChange);
      mediaSchemeQuery.addEventListener('change', onSystemChange);
    }
  }

  // Initialize event listeners for the radio buttons.
  radios.forEach(radio => {
    radio.addEventListener('change', e => {
      const target = e.target;

      if (!(target instanceof HTMLInputElement)) return;
      if (!target.checked) return;

      const value = target.value;

      if (value === 'system' && hasSystemOption) setMode('system');
      else if (value === 'dark' || value === 'light') setMode(value);
    });
  });

  // Initial state on page load
  const savedMode = localStorage.getItem(storageKey);

  if (savedMode === 'dark' || savedMode === 'light') {
    checkRadio(savedMode);
    mediaSchemeQuery.removeEventListener('change', onSystemChange);
  } else if (hasSystemOption) {
    checkRadio('system');
    mediaSchemeQuery.addEventListener('change', onSystemChange);
  } else {
    checkRadio(getSystemScheme());
  }
})();